
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslations } from "use-intl";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Invoice } from "@/types/payments";
import { Loader2, AlertTriangle } from "lucide-react";
import { AccountApiClient } from "@/clients/accountApiClient";
import { PaymentService } from "@/services/paymentService";
import { toast } from "sonner";
import { formatVendorAccount, hasVendorAccount } from "@/utils/formatVendorAccount";
import { formatPlainAmount } from "@/utils/paymentUtils";
import { useConsentContextOptional } from "@/components/providers/ConsentProvider";
import { accountLabel, renewalRequestFor } from "@/lib/renewalRequest";
import type { BankAccountSummary } from "@/types/accounts";
import { userFacingErrorMessage } from '@/utils/userFacingError';

const editPaymentSchema = z.object({
  sourceAccountId: z.string().optional(),
  paymentDate: z.string().optional(),
});

type EditPaymentFormData = z.infer<typeof editPaymentSchema>;

type PickableAccount = Pick<BankAccountSummary, "id" | "bankName" | "maskedNumber" | "currency" | "bic" | "status" | "resourceId"> & {
  name: string;
  accessNeeded: boolean;
};

interface EditPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: Invoice | null;
  onSuccess?: () => void;
}

const EditPaymentDialog: React.FC<EditPaymentDialogProps> = ({
  open,
  onOpenChange,
  invoice,
  onSuccess
}) => {
  const t = useTranslations("consultantPayments");
  // The latest translator, so the account loader can word an account name without being rebuilt.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);
  const [isLoading, setIsLoading] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<PickableAccount[]>([]);
  // Distinct from `bankAccounts.length > 0` — a company with zero connected accounts still
  // needs to be told "not loaded yet" vs "loaded, and there's nothing to resolve against".
  const [bankAccountsLoaded, setBankAccountsLoaded] = useState(false);
  const paymentService = new PaymentService();

  const form = useForm<EditPaymentFormData>({
    resolver: zodResolver(editPaymentSchema),
    defaultValues: {
      sourceAccountId: "",
      paymentDate: "",
    },
  });

  const consentContext = useConsentContextOptional();
  const setOnAccountsRefresh = consentContext?.setOnAccountsRefresh;

  const fetchBankAccounts = useCallback(async () => {
    const response = await AccountApiClient.getBankAccounts();
    setBankAccounts(
      response.accounts.map((account) => ({
        id: account.id,
        bankName: account.bankName,
        maskedNumber: account.maskedNumber,
        currency: account.currency,
        bic: account.bic,
        status: account.status,
        resourceId: account.resourceId,
        // Same dead-consent signal the Bank page renders as "Access needed" (FIN-863) --
        // 'unavailable' is a transient provider hiccup and deliberately not flagged here.
        accessNeeded: account.status === "consent_invalid",
        name: `[${account.currency}] ${account.bankName} - ${account.maskedNumber ?? tRef.current("edit.noAccountNumber")}`,
      })),
    );
  }, []);

  // True only once the fetch for the CURRENT open has settled. `bankAccountsLoaded` alone can't say
  // so: it is still true from the previous open on the reopen's first render, when the form would
  // otherwise be seeded from the previous open's account list.
  const fetchSettledForOpen = useRef(false);

  // Fetch bank accounts when dialog opens
  useEffect(() => {
    if (open) {
      fetchSettledForOpen.current = false;
      const loadBankAccounts = async () => {
        setBankAccountsLoaded(false);
        try {
          await fetchBankAccounts();
        } catch (error) {
          console.error("Failed to fetch bank accounts:", error);
        } finally {
          fetchSettledForOpen.current = true;
          setBankAccountsLoaded(true);
        }
      };
      loadBankAccounts();
    }
  }, [open, fetchBankAccounts]);

  // Renewing access (FIN-940) changes the account list underneath an open dialog: re-read it, but
  // leave the form alone — the consultant may already have edited the date or picked an account.
  useEffect(() => {
    if (!open || !setOnAccountsRefresh) return;
    const refreshAfterRenewal = async () => {
      try {
        await fetchBankAccounts();
      } catch (error) {
        console.error("Failed to refresh bank accounts after renewing access:", error);
        toast.error(tRef.current("edit.toasts.refreshFailed"));
      }
    };
    // Wrapped in a function: setOnAccountsRefresh is a useState setter, which would otherwise
    // call the callback as an updater (same as Bank.tsx).
    setOnAccountsRefresh(() => refreshAfterRenewal);
    return () => setOnAccountsRefresh(undefined);
  }, [open, setOnAccountsRefresh, fetchBankAccounts]);

  // A stored sourceAccountId only counts as resolved once we've actually loaded the connected
  // accounts to check it against — never hand an unresolvable id to the Select and rely on its
  // own normalisation to drop it (FIN-933). Seeded once per open: an account-list refresh must not
  // reset what the consultant has since changed.
  const seededForInvoiceId = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      seededForInvoiceId.current = null;
      return;
    }
    if (invoice && bankAccountsLoaded && fetchSettledForOpen.current && seededForInvoiceId.current !== invoice.id) {
      seededForInvoiceId.current = invoice.id;
      const resolvedSourceAccountId =
        invoice.sourceAccountId && bankAccounts.some((acc) => acc.id === invoice.sourceAccountId)
          ? invoice.sourceAccountId
          : "";
      form.reset({
        sourceAccountId: resolvedSourceAccountId,
        paymentDate: invoice.payablePaymentDate || invoice.dueDate || "",
      });
    }
  }, [open, invoice, bankAccounts, bankAccountsLoaded, form]);

  const onSubmit = async (data: EditPaymentFormData) => {
    if (!invoice) return;

    setIsLoading(true);
    try {
      console.log("EditPaymentDialog: Submitting payment update", {
        invoiceId: invoice.id,
        payableId: invoice.payableId,
        data
      });

      // `invoice.id` is the PAYABLE's id, not the financial_document's — getPayables maps
      // through mapPayableRowToPayable, which sets `id: payable.id` and puts
      // the document id on a separate `invoiceId`. Sending it as `invoice_id` made the backend
      // look for `payables.invoice_id = <a payable id>`, which never matches, so every save
      // failed with "No payable found for invoice ID". Sent as `id` instead, which is the
      // branch that resolves a payable by its own primary key.
      // FIN-933: re-validate against the currently loaded accounts here rather than trusting
      // that `data.sourceAccountId` can only ever be a real id — that trust previously depended on
      // Radix's Select silently coercing an unmatched controlled value to empty.
      const resolvedSourceAccountId =
        data.sourceAccountId && bankAccounts.some((acc) => acc.id === data.sourceAccountId)
          ? data.sourceAccountId
          : null;

      await paymentService.updatePayable({
        id: invoice.id,
        vendor_iban: invoice.vendorIban,
        vendor_bankgiro: invoice.vendorBankgiro,
        vendor_plusgiro: invoice.vendorPlusgiro,
        vendor_account_number: invoice.vendorAccountNumber,
        vendor_clearing_number: invoice.vendorClearingNumber,
        source_account_id: resolvedSourceAccountId,
        payment_date: data.paymentDate,
      });

      console.log("EditPaymentDialog: Payable updated successfully");
      toast.success(t("edit.toasts.updated"));

      // Close the dialog and call success callback
      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("EditPaymentDialog: Error updating payment details:", error);
      const errorMessage = userFacingErrorMessage(error, t("edit.toasts.updateFailed"));
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    form.reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("edit.title")}</DialogTitle>
        </DialogHeader>
        
        {invoice && (
          <div className="mb-4 p-3 bg-muted rounded-lg">
            <div className="text-sm text-muted-foreground">{t("edit.invoice")}</div>
            <div className="font-medium">{invoice.vendor}</div>
            <div className="text-sm">{formatPlainAmount(invoice.amount, invoice.currency)}</div>
            {hasVendorAccount(invoice) && (
              <div className="text-sm font-mono text-muted-foreground mt-1">
                {formatVendorAccount(invoice)}
              </div>
            )}
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="sourceAccountId"
              render={({ field }) => {
                const selectedAccount = bankAccounts.find(acc => acc.id === field.value);
                const selectedNeedsAccess = Boolean(selectedAccount?.accessNeeded);
                const isSourceAccountUnresolvable =
                  bankAccountsLoaded &&
                  !field.value &&
                  Boolean(invoice?.sourceAccountId) &&
                  !bankAccounts.some((acc) => acc.id === invoice?.sourceAccountId);
                return (
                  <FormItem>
                    <FormLabel>{t("edit.sourceAccount")}</FormLabel>
                    {isSourceAccountUnresolvable && (
                      <div className="flex items-start gap-1.5 text-sm text-amber-600">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>{t("edit.unresolvable")}</span>
                      </div>
                    )}
                    {selectedAccount && selectedNeedsAccess && (
                      <div className="flex flex-wrap items-start gap-x-1.5 text-sm text-amber-600">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>{t("access.expired", { account: accountLabel(selectedAccount) })}</span>
                        {consentContext && (
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="h-auto p-0 font-semibold underline underline-offset-2"
                            onClick={() => consentContext.setConsentRequiredError(renewalRequestFor([selectedAccount]))}
                          >
                            {t("access.renew")}
                          </Button>
                        )}
                      </div>
                    )}
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || undefined}
                    >
                      <FormControl>
                        <SelectTrigger className="text-left">
                          <SelectValue className="text-left" placeholder={t("edit.selectAccount")}>
                            {selectedAccount?.name || t("edit.selectAccount")}
                          </SelectValue>
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {bankAccounts.length > 0 ? (
                          bankAccounts.map((account) => (
                            // A disconnected account stays listed (so a payable already pointed at it
                            // stays visible) but cannot be newly picked.
                            <SelectItem
                              key={account.id}
                              value={account.id}
                              disabled={account.accessNeeded && account.id !== field.value}
                            >
                              {account.name}
                              {account.accessNeeded && (
                                <span className="ml-2 text-xs text-destructive">{t("access.needed")}</span>
                              )}
                            </SelectItem>
                          ))
                        ) : (
                          <div className="px-2 py-1.5 text-sm text-muted-foreground">{t("edit.noAccounts")}</div>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            <FormField
              control={form.control}
              name="paymentDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("edit.paymentDate")}</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                {t("edit.cancel")}
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("edit.updating")}
                  </>
                ) : (
                  t("edit.update")
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditPaymentDialog;
